import { useEffect, useState, useCallback, useRef } from 'react';
import { Phone, PhoneOff, PhoneIncoming, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router';
import { apiGet } from '../../hooks/useApi';
import { formatPhone } from '../../../lib/phone-format';

interface PendingIncoming {
  callId: string;
  from: string;
  to: string;
  contactName?: string;
  /** Transferring agent's display name — set on transfer destination rings. */
  initiatorName?: string;
  /** Caller identity enriched server-side by apex-voice (#1049) — names only, no PII records (#653). */
  contactId?: string;
  agencyName?: string;
  brandName?: string;
}

interface ContactMatch {
  id: string;
  name: string | null;
  agencyName: string | null;
  brandName?: string | null;
}

/**
 * Human-readable destination/source label. Phone-shaped values render as
 * `(502) 744-6660`; non-phone values (`client:rep_two` SIP-style targets)
 * pass through untouched.
 */
function formatEndpoint(value: string): string {
  if (!/^\+?[\d\s().-]+$/.test(value)) return value;
  return formatPhone(value) || value;
}

export function InboundCallToast() {
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<PendingIncoming | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  // Contacts whose phone matches the caller, resolved while the toast is open.
  // `count` is the true total (may exceed contacts.length, which is capped);
  // null until the lookup resolves. Drives both click-to-open (one match) and
  // the duplicate-caller disambiguation list (many matches).
  const [matches, setMatches] = useState<{ count: number; contacts: ContactMatch[] } | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const ringIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevCallIdRef = useRef<string | null>(null);
  // Tracks whether the user initiated answer/reject — suppresses "handled by another agent" flash
  const selfActionRef = useRef(false);
  const isOnCallRef = useRef(false);

  // Subscribe to apex:call-incoming DOM events from softphone.ts.
  //
  // Also subscribes to apex:call-update so that once a primaryCall exists
  // (the agent is actively on a call — whether they picked this one up, or
  // answered via keyboard, or a warm-transfer pickup resolved), we force-
  // dismiss any incoming state that somehow lingered. Without this, the
  // ringtone continues on warm-transfer pickup when apex-voice re-publishes
  // a ring-leg event for the newly-joined conference (GLAC-style race where
  // the ring state and the active-call state both exist for a heartbeat).
  useEffect(() => {
    const handler = (e: Event) => {
      if (isOnCallRef.current) return;
      const detail = (e as CustomEvent).detail as PendingIncoming | null;

      // If we had an incoming call that just disappeared and the user didn't
      // initiate it, show a brief "handled by another agent" flash
      if (!detail && prevCallIdRef.current) {
        if (selfActionRef.current) {
          selfActionRef.current = false;
        } else {
          setDismissed('Call handled by another agent');
          setTimeout(() => setDismissed(null), 2000);
        }
      }

      prevCallIdRef.current = detail?.callId || null;
      setIncoming(detail);
      // Reset — the new caller's matches are unknown until the lookup below runs.
      setMatches(null);
    };

    const updateHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { phase?: string } | null;
      const active = !!(detail && detail.phase !== 'ended');
      isOnCallRef.current = active;
      // An active call of any phase means this agent is on a call now;
      // there should never be an unresolved incoming toast in that window.
      // Dismiss unconditionally — if a real new inbound lands later,
      // apex:call-incoming will resurface it with fresh state.
      if (active) {
        prevCallIdRef.current = null;
        setIncoming(null);
        setMatches(null);
      }
    };

    window.addEventListener('apex:call-incoming', handler);
    window.addEventListener('apex:call-update', updateHandler);
    return () => {
      window.removeEventListener('apex:call-incoming', handler);
      window.removeEventListener('apex:call-update', updateHandler);
    };
  }, []);

  // Resolve caller → matching contacts. apex-voice events carry at most a
  // single primary name; here we fetch the full match set so the card can (a)
  // jump straight to the contact when there's exactly one, and (b) warn the
  // rep + list candidates when one number maps to several contacts. Scoped to
  // the rep's visible agencies server-side. Best-effort: on failure the card
  // falls back to the raw number.
  useEffect(() => {
    if (!incoming?.from) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet<{ count: number; contacts: ContactMatch[] }>(
          `/api/contacts/by-phone?phone=${encodeURIComponent(incoming.from)}`,
        );
        if (cancelled) return;
        setMatches({ count: res.count ?? 0, contacts: res.contacts ?? [] });
      } catch {
        // Swallow — leaving matches null means the card just shows the number.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [incoming?.from]);

  // Ringtone — repeating ring pattern via Web Audio API
  useEffect(() => {
    if (!incoming) {
      // Stop ringtone
      if (ringIntervalRef.current) {
        clearInterval(ringIntervalRef.current);
        ringIntervalRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
      return;
    }

    // Start ringtone
    try {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;

      // Resume if suspended (requires prior user gesture)
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }

      const playRingBurst = () => {
        if (ctx.state === 'closed') return;
        // Two-tone ring: 440Hz + 480Hz (standard US ring)
        const now = ctx.currentTime;
        for (const freq of [440, 480]) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.08, now);
          gain.gain.setValueAtTime(0.08, now + 0.8);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
          osc.start(now);
          osc.stop(now + 1.0);
        }
      };

      playRingBurst();
      // US ring cadence: 1s burst every 3s
      ringIntervalRef.current = setInterval(playRingBurst, 3000);
    } catch {
      // Web Audio not available
    }

    return () => {
      if (ringIntervalRef.current) {
        clearInterval(ringIntervalRef.current);
        ringIntervalRef.current = null;
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, [incoming]);

  // Browser push notification
  useEffect(() => {
    if (!incoming) return;

    if (Notification.permission === 'granted') {
      try {
        // Body mirrors the toast: who is calling (name → formatted number →
        // "Unknown caller"), their agency · brand when known, then the number.
        const lines: string[] = [
          incoming.contactName ||
            (incoming.from ? formatEndpoint(incoming.from) : '') ||
            'Unknown caller',
        ];
        const org = [incoming.agencyName, incoming.brandName].filter(Boolean).join(' · ');
        if (org) lines.push(org);
        if (incoming.contactName && incoming.from) lines.push(formatEndpoint(incoming.from));
        const n = new Notification(
          incoming.initiatorName ? `Transfer from ${incoming.initiatorName}` : 'Incoming Call',
          {
            body: lines.join('\n'),
            icon: '/brand/apex-favicon.svg',
            tag: `call-${incoming.callId || 'pending'}`,
            requireInteraction: true,
          },
        );
        return () => n.close();
      } catch {
        // Notification API not available
      }
    }
  }, [incoming]);

  const handleAnswer = useCallback(() => {
    selfActionRef.current = true;
    // Stop the ringtone and tear down the toast immediately — don't wait for
    // the softphone's apex:call-incoming(null) broadcast to arrive. That
    // broadcast races with apex-voice's post-answer events (ring leg on the
    // conference, call.answered re-ring, etc.) and can briefly put the UI
    // back into a ringing state, leaving the ringtone audible after pickup.
    // The local clear is deterministic; softphone state catches up in the
    // next event cycle.
    setIncoming(null);
    prevCallIdRef.current = null;
    const sp = (window as unknown as Record<string, unknown>)['ApexSoftphone'] as
      | { answer: () => void }
      | undefined;
    sp?.answer();
  }, []);

  const handleReject = useCallback(() => {
    selfActionRef.current = true;
    // Same reasoning as handleAnswer — kill the ringtone on click, don't
    // wait for the async event.
    setIncoming(null);
    prevCallIdRef.current = null;
    const sp = (window as unknown as Record<string, unknown>)['ApexSoftphone'] as
      | { reject: () => void }
      | undefined;
    sp?.reject();
  }, []);

  /**
   * Open a contact profile from the card. Navigate-only — the ringtone and
   * softphone state stay intact, so this works as a "who is this?" lookup
   * before answering.
   */
  const handleOpenContact = useCallback(
    (id: string) => {
      navigate(`/contacts/${id}`);
    },
    [navigate],
  );

  // Placeholder entries (callId: '') cannot be answered yet — answer() blocks on empty callId
  const isPlaceholder = incoming?.callId === '';
  const matchCount = matches?.count ?? 0;
  const candidates = matches?.contacts ?? [];
  // More than one contact shares this number — don't assert a single identity;
  // surface the count + a list so the rep knows to ask who's calling.
  const isAmbiguous = matchCount > 1;
  const single = matchCount === 1 ? (candidates[0] ?? null) : null;
  // Exactly one match → the card is click-to-open. apex-voice's instant
  // contact_id/contactName cover the gap before this lookup resolves. Never
  // clickable when ambiguous — don't assert an arbitrary identity.
  const singleContactId = isAmbiguous ? null : incoming?.contactId || single?.id || null;

  // Caller identity (#1049): prefer the instant WS enrichment from
  // apex-voice's call.ringing, then the by-phone lookup that resolves while
  // the toast is open. Suppressed when several contacts share the number.
  const contactName = !isAmbiguous ? incoming?.contactName || single?.name || null : null;
  const orgLine = !isAmbiguous
    ? [incoming?.agencyName || single?.agencyName, incoming?.brandName || single?.brandName]
        .filter(Boolean)
        .join(' · ')
    : '';
  const formattedFrom = incoming?.from ? formatEndpoint(incoming.from) : '';

  // Display priority: "N contacts" when ambiguous → resolved contact name →
  // "Unknown caller" once the lookup confirms no match (while it's still
  // resolving, show the formatted number to avoid an "Unknown caller" →
  // name flicker). The number always rides along as a sub-line; on transfer
  // rings the label carries the transferring agent's name so the rep sees
  // both who's calling and who's handing the call off.
  const displayName = isAmbiguous
    ? `${matchCount} contacts`
    : (contactName ?? (matches !== null ? 'Unknown caller' : formattedFrom || 'Unknown caller'));
  const showNumber = !!incoming?.from && displayName !== formattedFrom;
  const ringLabel = incoming?.initiatorName
    ? `Transfer from ${incoming.initiatorName}`
    : 'Incoming Call';

  return (
    <div className="fixed bottom-6 right-6 z-[9999] pointer-events-none">
      <AnimatePresence>
        {incoming && (
          <motion.div
            key="inbound-call"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.9 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="pointer-events-auto bg-card border-2 border-brand-green/40 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.18)] p-5 w-[320px]"
          >
            {/* Pulsing ring indicator + name row. Clickable when exactly one
                contact is resolved so the rep can jump to the profile without
                answering first. When the number maps to several contacts this
                short-lived toast shows the count only — the rep picks the right
                one from the in-call modal after answering and asking. */}
            <button
              type="button"
              onClick={() => singleContactId && handleOpenContact(singleContactId)}
              disabled={!singleContactId}
              aria-label={singleContactId ? 'Open contact profile' : 'Caller details'}
              className={`w-full flex items-center gap-3 mb-4 text-left rounded-lg -m-1 p-1 transition-colors ${
                singleContactId ? 'hover:bg-secondary/40 cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-brand-green/15 flex items-center justify-center">
                  {isAmbiguous ? (
                    <Users className="w-6 h-6 text-brand-green" />
                  ) : (
                    <PhoneIncoming className="w-6 h-6 text-brand-green" />
                  )}
                </div>
                <span className="absolute inset-0 rounded-full bg-brand-green/20 animate-ping" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[0.625rem] uppercase font-bold text-brand-green tracking-wider mb-0.5">
                  {isPlaceholder ? 'Connecting...' : ringLabel}
                </p>
                <p className="text-[0.9375rem] font-semibold text-foreground truncate">
                  {displayName}
                </p>
                {/* #1049 — the matched contact's agency · brand, so the rep
                    knows the calling context before picking up. */}
                {orgLine && (
                  <p className="text-[0.75rem] text-muted-foreground truncate">{orgLine}</p>
                )}
                {showNumber && (
                  <p className="text-[0.75rem] text-muted-foreground truncate">{formattedFrom}</p>
                )}
                {/* #485 — which of the user's numbers the caller reached.
                    Matters for users with multiple inbound lines (group,
                    shared, direct) so they can answer in context. */}
                {incoming?.to && (
                  <p className="text-[0.6875rem] text-muted-foreground truncate font-mono">
                    to {formatEndpoint(incoming.to)}
                  </p>
                )}
              </div>
            </button>

            {/* Answer / Reject buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleAnswer}
                disabled={isPlaceholder}
                aria-label="Answer call"
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-semibold text-[0.8125rem] transition-colors shadow-sm ${
                  isPlaceholder
                    ? 'bg-brand-green/40 cursor-not-allowed'
                    : 'bg-brand-green hover:bg-brand-green/90 cursor-pointer'
                }`}
              >
                <Phone className="w-4 h-4" />
                {isPlaceholder ? 'Connecting...' : 'Answer'}
              </button>
              <button
                onClick={handleReject}
                aria-label="Reject call"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-destructive hover:bg-destructive/90 text-white font-semibold text-[0.8125rem] transition-colors cursor-pointer shadow-sm"
              >
                <PhoneOff className="w-4 h-4" />
                Reject
              </button>
            </div>
          </motion.div>
        )}

        {/* Brief dismissal notice (another agent answered, call ended, etc.) */}
        {!incoming && dismissed && (
          <motion.div
            key="dismissed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.25 }}
            className="pointer-events-auto bg-card border border-border/60 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.1)] px-4 py-3 text-[0.75rem] text-muted-foreground"
          >
            {dismissed}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
